<template>
  <div class="file-upload-container">
    <label :for="id" class="file-upload-label">{{ label }}</label>
    <div class="upload-button-container">
      <b-button
        variant="primary"
        size="sm"
        :disabled="disabled || maxFilesReached"
        class="upload-button"
      >
        <feather-icon icon="UploadIcon" size="18" />
        <input
          :id="id"
          type="file"
          @change="handleFileChange"
          multiple
          :disabled="disabled || maxFilesReached"
          class="file-input"
        />
      </b-button>
      <div
        v-if="allowPaste && !disabled"
        class="paste-image-container"
        :class="{ 'paste-image-container-disabled': maxFilesReached }"
        tabindex="0"
        @paste="handlePaste"
      >
        {{ $t("maintenance.file_upload.paste_image") }}
      </div>
    </div>
    <div v-if="minFiles !== null || maxFiles !== null" class="file-limit-info">
      {{ totalFiles }}
      <span v-if="maxFiles !== null"> / {{ maxFiles }}</span>
      {{ $t("maintenance.file_upload.images") }}
      <span v-if="minFiles !== null">· {{ $t("maintenance.file_upload.minimum", { min: minFiles }) }}</span>
    </div>
    <div
      v-if="minFiles !== null && totalFiles < minFiles"
      class="file-limit-error"
    >
      {{ $t("maintenance.file_upload.min_images_required", { min: minFiles }) }}
    </div>
    <div class="previews" v-if="previews.length || existingPreviews.length">
      <div
        v-for="(preview, index) in existingPreviews"
        :key="'existing-' + index"
        class="preview-container"
      >
        <div class="preview-content">
          <img
            :src="preview.url"
            class="preview-image"
            @click="viewImage(preview.url)"
          >
          <button
            class="remove-button"
            @click="removeExistingFile(index)"
            :disabled="disabled"
          >
            &#10005;
          </button>
        </div>
      </div>
      <div
        v-for="(preview, index) in previews"
        :key="index"
        class="preview-container"
      >
        <div class="preview-content">
          <img
            :src="preview"
            class="preview-image"
            @click="viewImage(preview)"
          >
          <button
            class="remove-button"
            @click="removeFile(index)"
            :disabled="disabled"
          >
            &#10005;
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  props: {
    id: String,
    name: String,
    label: String,
    multiple: Boolean,
    disabled: {
      type: Boolean,
      default: false
    },
    allowPaste: {
      type: Boolean,
      default: false
    },
    minFiles: {
      type: Number,
      default: null
    },
    maxFiles: {
      type: Number,
      default: null
    },
    onUpdateFiles: Function,
    existingFiles: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      files: [],
      previews: [],
      existingPreviews: []
    };
  },
  computed: {
    totalFiles() {
      return this.files.length + this.existingPreviews.length;
    },
    maxFilesReached() {
      return this.maxFiles !== null && this.totalFiles >= this.maxFiles;
    }
  },
  watch: {
    existingFiles: {
      immediate: true,
      handler(newVal) {
        this.existingPreviews = Array.isArray(newVal) ? [...newVal] : [];
        this.$nextTick(() => {
          this.emitFiles();
        });
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.emitFiles();
    });
  },
  methods: {
    handleFileChange(event) {
      if (this.disabled || this.maxFilesReached) return;
      const newFiles = Array.from(event.target.files || []);
      this.addFiles(newFiles);
      event.target.value = "";
    },
    handlePaste(event) {
      if (this.disabled || !this.allowPaste || this.maxFilesReached) return;
      const clipboardItems =
        event.clipboardData && event.clipboardData.items
          ? Array.from(event.clipboardData.items)
          : [];
      const pastedFiles = [];
      clipboardItems.forEach(clipboardItem => {
        const isImage =
          clipboardItem.kind === "file" &&
          clipboardItem.type &&
          clipboardItem.type.startsWith("image/");
        if (!isImage) return;
        const clipboardFile = clipboardItem.getAsFile();
        if (!clipboardFile) return;
        pastedFiles.push(this.createPastedImageFile(clipboardFile));
      });
      if (pastedFiles.length === 0) return;
      event.preventDefault();
      this.addFiles(pastedFiles);
    },
    createPastedImageFile(clipboardFile) {
      const extension = this.getFileExtensionFromMimeType(clipboardFile.type);
      const fileName = `pasted-image-${Date.now()}.${extension}`;
      return new File(
        [clipboardFile],
        fileName,
        {
          type: clipboardFile.type || "image/png",
          lastModified: Date.now()
        }
      );
    },
    getFileExtensionFromMimeType(mimeType) {
      const extensions = {
        "image/png": "png",
        "image/jpeg": "jpg",
        "image/jpg": "jpg",
        "image/webp": "webp",
        "image/gif": "gif"
      };
      return extensions[mimeType] || "png";
    },
    addFiles(newFiles) {
      if (!Array.isArray(newFiles) || newFiles.length === 0) return;
      let filesToAdd = newFiles;
      if (this.maxFiles !== null) {
        const availableFiles = this.maxFiles - this.totalFiles;
        if (availableFiles <= 0) return;
        filesToAdd = newFiles.slice(0, availableFiles);
      }
      this.files.push(...filesToAdd);
      this.previews.push(
        ...filesToAdd.map(file => URL.createObjectURL(file))
      );
      this.emitFiles();
    },
    removeFile(index) {
      if (this.disabled) return;
      if (this.previews[index]) {
        URL.revokeObjectURL(this.previews[index]);
      }
      this.files.splice(index, 1);
      this.previews.splice(index, 1);
      this.emitFiles();
    },
    removeExistingFile(index) {
      if (this.disabled) return;
      console.log("Removing existing file at index:", index);
      console.log(
        "Before removal, existingPreviews:",
        JSON.stringify(this.existingPreviews, null, 2)
      );
      this.existingPreviews.splice(index, 1);
      console.log(
        "After removal, existingPreviews:",
        JSON.stringify(this.existingPreviews, null, 2)
      );
      this.emitFiles();
    },
    viewImage(url) {
      console.log("Viewing image URL:", url);
      window.open(url, "_blank");
    },
    emitFiles() {
      const fileData = this.files.map(file => ({
        url: "",
        name: file.name
      }));
      if (this.onUpdateFiles) {
        this.onUpdateFiles({
          id: this.id,
          files: fileData,
          label: this.label,
          rawFiles: this.files,
          existingFiles: this.existingPreviews
        });
      }
    }
  }
};
</script>
<style>
.file-upload-container {
  margin-top: 10px;
}
.file-upload-label {
  display: block;
  line-height: 1.5;
  font-size: 0.9em;
  font-weight: bolder;
  margin-bottom: 5px;
}
.upload-button-container {
  display: flex;
  align-items: center;
}
.upload-button {
  position: relative;
}
.file-input {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  cursor: pointer;
}
.paste-image-container {
  margin-left: 10px;
  padding: 5px 10px;
  border: 1px dashed #b8b8b8;
  border-radius: 4px;
  cursor: text;
  font-size: 0.85rem;
  color: #6e6b7b;
  outline: none;
}
.paste-image-container:hover,
.paste-image-container:focus {
  border-color: #7367f0;
}
.paste-image-container-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.file-limit-info {
  margin-top: 6px;
  font-size: 0.8rem;
  color: #6e6b7b;
}
.file-limit-error {
  margin-top: 4px;
  font-size: 0.8rem;
  color: #dc3545;
}
.previews {
  display: flex;
  flex-wrap: wrap;
  margin-top: 10px;
}
.preview-container {
  position: relative;
  margin-right: 10px;
  margin-bottom: 10px;
}
.preview-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}
.preview-image {
  max-width: 100px;
  max-height: 100px;
  cursor: pointer;
  margin-bottom: 5px;
}
.remove-button {
  position: absolute;
  top: -5px;
  right: -5px;
  background-color: red;
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  line-height: 14px;
  padding: 0;
}
.remove-button:disabled {
  background-color: gray;
  cursor: not-allowed;
}
</style>
